import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { FileText, Loader2 } from "lucide-react";

// Use pinned CDN worker to avoid webpack/craco worker config in CRA.
// Version must match the installed pdfjs-dist (see package.json).
pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs";

/**
 * Renders the first page of a PDF (base64 data URL) onto a canvas.
 * Falls back to a clean icon tile if rendering fails.
 *
 * Props:
 *   src       - PDF data URL (data:application/pdf;base64,...) OR plain URL
 *   className - applied to wrapper div (size/aspect)
 *   label     - optional caption shown below thumbnail in fallback mode
 */
export default function PdfThumbnail({ src, className = "", label = "PDF" }) {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ok | error

  useEffect(() => {
    let cancelled = false;
    let renderTask = null;
    async function render() {
      if (!src) return;
      setStatus("loading");
      try {
        let data;
        if (src.startsWith("data:")) {
          const b64 = src.split(",")[1] || "";
          const bin = atob(b64);
          const arr = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
          data = arr;
        } else {
          data = src;
        }
        const loadingTask = pdfjsLib.getDocument({ data });
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        const page = await pdf.getPage(1);
        const canvas = canvasRef.current;
        if (!canvas) return;
        // Render at sensible size
        const viewportBase = page.getViewport({ scale: 1 });
        const targetW = Math.min(640, canvas.parentElement?.clientWidth || 320);
        const scale = targetW / viewportBase.width;
        const viewport = page.getViewport({ scale });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d");
        renderTask = page.render({ canvasContext: ctx, viewport });
        await renderTask.promise;
        if (!cancelled) setStatus("ok");
      } catch (e) {
        if (!cancelled) setStatus("error");
        // eslint-disable-next-line no-console
        console.warn("PDF thumb render failed", e);
      }
    }
    render();
    return () => {
      cancelled = true;
      try {
        renderTask?.cancel?.();
      } catch (e) {
        // ignore cancel errors
      }
    };
  }, [src]);

  return (
    <div
      className={`relative w-full h-full bg-white flex items-center justify-center overflow-hidden ${className}`}
      data-testid="pdf-thumbnail"
    >
      <canvas
        ref={canvasRef}
        className={`w-full h-full object-contain ${status === "ok" ? "" : "hidden"}`}
      />
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center text-stone-400 bg-stone-50">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-stone-600 bg-stone-50 p-4 text-center">
          <FileText className="w-10 h-10" strokeWidth={1.2} />
          <span className="text-xs uppercase tracking-[0.18em] font-semibold">
            {label}
          </span>
        </div>
      )}
      {status === "ok" && (
        <span className="absolute bottom-2 left-2 bg-stone-900 text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1 pointer-events-none">
          <FileText className="w-2.5 h-2.5" strokeWidth={2} />
          PDF
        </span>
      )}
    </div>
  );
}
